import { useEffect, useRef } from "react";

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

// Keyboard behaviour every modal dialog / drawer needs: focus moves INTO it when it opens,
// Tab cycles inside it (never out to the page behind), Escape closes it, and focus goes back to
// the control that opened it. Attach the returned ref to the element with role="dialog".
export default function useDialogFocus(onClose) {
  const ref = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const previous = document.activeElement;
    const isShown = (el) => !el.closest("[hidden],[aria-hidden=\"true\"]") && (typeof el.checkVisibility !== "function" || el.checkVisibility());
    const focusables = () => [...node.querySelectorAll(FOCUSABLE)].filter(isShown);
    const first = focusables()[0];
    if (first) first.focus(); else { node.tabIndex = -1; node.focus(); }

    const onKey = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); closeRef.current?.(); return; }
      if (e.key !== "Tab") return;
      const items = focusables();
      if (items.length === 0) { e.preventDefault(); return; }
      const active = document.activeElement;
      const outside = !node.contains(active);
      if (e.shiftKey && (outside || active === items[0])) { e.preventDefault(); items[items.length - 1].focus(); }
      else if (!e.shiftKey && (outside || active === items[items.length - 1])) { e.preventDefault(); items[0].focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (previous && typeof previous.focus === "function" && document.contains(previous)) previous.focus();
    };
  }, []);

  return ref;
}
